import type { Color } from './types';
import Toastify from 'toastify-js';
import 'toastify-js/src/toastify.css';

export function setupErrorHandler() {
  window.addEventListener("error", (event: ErrorEvent) => {
    showError("Unexpected error: " + event.error)
    event.preventDefault()
  })
  window.addEventListener('unhandledrejection', _ => {
    // event.promise  → the Promise that rejected
    // event.reason   → the value it rejected with (usually an Error)
    showError("Unexpected error", false);
    //event.preventDefault();  //keep default logging
  });

}

export function showError(userMessage: any, logMessage?: any)
{
  if (logMessage !== false)  console.error(logMessage || userMessage)
  let toast = Toastify({
    text: userMessage || "Unexpected error",
    duration: -1,
    backgroundColor: "#b31c2f",
    stopOnFocus: true,
    onClick: () => toast.hideToast()
  })
  toast.showToast();
}

export function showWarning(message: string)
{
  let toast = Toastify({
    text: message,
    duration: 5000,
    backgroundColor: '#F5A623',
    stopOnFocus: true,
    onClick: () => toast.hideToast()
  })
  toast.showToast();
}

export function showInfo(message: string)
{
  let toast = Toastify({
    text: message,
    duration: 2000,
    backgroundColor: '#4A90E2',
    stopOnFocus: true,
    onClick: () => toast.hideToast()
  })
  toast.showToast();
}

export function showQuestion(message: string, buttons: {[key: string]: () => void})
{
  const container = document.createElement('div');
  container.className = 'toastify-content';
  const buttonsRow = document.createElement('div');
  const textSpan = document.createElement('span');
  textSpan.textContent = message
  container.appendChild(textSpan);
  container.appendChild(buttonsRow);

  for (const buttonTitle in buttons) {
    const btn = document.createElement('button');
    btn.className = 'toastify-btn';
    btn.textContent = buttonTitle;
    btn.onclick = () => {
      toast.hideToast()
      buttons[buttonTitle]()
    }
    buttonsRow.appendChild(btn);
  }

  let toast = Toastify({
    node: container,
    duration: -1,
    backgroundColor: '#F5A623'
  })
  toast.showToast();
}

export function stripSuffix(s: string, suffix: string) {
    return s.endsWith(suffix) ? s.slice(0, -suffix.length) : s;
}

export function groupBy<T, V>(array: T[], keyFn: (it: T) => string, valueFn: (it: T) => V): {[key: string]: V} {
  return array.reduce((acc, it) => (acc[keyFn(it)] = valueFn(it), acc), {} as {[key: string]: V})
}

export function parseColor(hex: string): Color {
  if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex)) {
    throw new Error('Invalid hex color: ' + hex);
  }
  const clean = hex.slice(1);
  const fullHex = clean.length === 3
    ? clean.split('').map(ch => ch + ch).join('')
    : clean;
  const r = parseInt(fullHex.slice(0, 2), 16);
  const g = parseInt(fullHex.slice(2, 4), 16);
  const b = parseInt(fullHex.slice(4, 6), 16);
  return { r, g, b };
}

export function colorToString(color: Color): string {
  const r = color.r.toString(16).padStart(2, '0');
  const g = color.g.toString(16).padStart(2, '0');
  const b = color.b.toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

export function mixColors(c1: Color, c2: Color, weight: number = 0.5): Color {
  const w2 = Math.max(0, Math.min(1, weight));
  const w1 = 1 - w2;
  return {
    r: Math.round(c1.r * w1 + c2.r * w2),
    g: Math.round(c1.g * w1 + c2.g * w2),
    b: Math.round(c1.b * w1 + c2.b * w2),
  };
}

export function escapeHtml(text: string): string {
  if (text == null)  return '';
  return text.toString()
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
}